<template>
	<div v-if="show">
		<ja-modal
			ref="modal"
			class="message-modal"
			:show="!showMessageResult"
			@close-modal="closeModal"
		>
			<div class="modal-content">
				<h1>{{ $t('sendMessage') }}</h1>
				<ja-input
					v-model="topic"
					:max-length="MESSAGE_TOPIC_MAX_LENGTH"
					:label="$t('topic')"
					:placeholder="$t('enterYourTopic')"
					name="topic"
				></ja-input>
				<ja-textarea
					v-model="message"
					:max-length="MESSAGE_TEXT_MAX_LENGTH"
					:label="$t('freeTextLabel')"
					:placeholder="$t('messagePlaceholder')"
					name="freeMsgText"
				></ja-textarea>
				<div class="button-wrapper">
					<ja-button
						class="small"
						:disabled="sendButtonIsDisabled"
						name="sendMsg"
						@click.native="sendMessage"
						>{{ $t('send') }}</ja-button
					>
				</div>
			</div>
			<ja-close-button
				name="close"
				@click.native="closeModal"
			></ja-close-button>
		</ja-modal>
		<ja-modal
			ref="resultModal"
			tabindex="0"
			class="message-modal result-modal"
			:show="showResultModal"
			@close-modal="closeModal"
		>
			<div class="modal-content">
				<h1
					class="heading-5"
					:class="requestResult"
					:aria-live="requestResult == 'success' ? 'polite' : 'assertive'"
				>
					{{ messageResult }}
				</h1>
				<div class="heading-1 result-topic">{{ topic }}</div>
				<div class="body-text-2 result-message">{{ message }}</div>
				<div class="button-wrapper">
					<div v-if="requestResult === 'success'">
						<ja-button class="small" name="done" @click.native="closeModal">{{
							$t('done')
						}}</ja-button>
					</div>
					<div v-else>
						<ja-button
							class="try-again small"
							name="tryAgain"
							@click.native="sendMessage"
							>{{ $t('tryAgain') }}</ja-button
						><ja-button
							class="cancel small outlined"
							name="cancel"
							@click.native="closeModal"
							>{{ $t('cancel') }}</ja-button
						>
					</div>
				</div>
			</div>
			<ja-close-button
				name="close"
				@click.native="closeModal"
			></ja-close-button>
		</ja-modal>
	</div>
</template>

<script>
import gql from 'graphql-tag';
import '../icons/expenseCategories';
import UserData from '@/mixins/UserData';
import ModalEvents from '@/mixins/ModalEvents';
import Modal from '@/components/Modal/Modal';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/form/Input';
import Textarea from '@/components/ui/form/Textarea';
import CloseButton from '@/components/ui/CloseButton';

export default {
	components: {
		jaModal: Modal,
		jaInput: Input,
		jaTextarea: Textarea,
		jaButton: Button,
		jaCloseButton: CloseButton
	},
	mixins: [UserData, ModalEvents],
	props: {
		show: {
			type: Boolean,
			value: false
		}
	},
	data() {
		return {
			topic: '',
			message: '',
			showMessageResult: false,
			requestResult: null
		};
	},
	computed: {
		constants() {
			return this.$store.getters.constants;
		},
		MESSAGE_TOPIC_MAX_LENGTH() {
			if (this.constants) {
				return this.constants.messageTopicMaxLength;
			}
			return null;
		},
		MESSAGE_TEXT_MAX_LENGTH() {
			if (this.constants) {
				return this.constants.messageTextMaxLength;
			}
			return null;
		},
		MESSAGE_TEXT_MIN_LENGTH() {
			if (this.constants) {
				return this.constants.messageTextMinLength;
			}
			return null;
		},
		sendButtonIsDisabled() {
			return (
				this.topic.trim() &&
				this.message.trim().length < this.MESSAGE_TEXT_MIN_LENGTH
			);
		},
		messageResult() {
			return this.requestResult == 'success'
				? this.$t('messageSentSuccessfully')
				: this.$t('messageSendingFailed');
		},
		showResultModal() {
			return this.requestResult && this.showMessageResult;
		}
	},
	watch: {
		showResultModal(value) {
			if (value) {
				setTimeout(() => {
					this.$refs.resultModal.$el.focus();
				}, 200);
			}
		}
	},
	methods: {
		closeModal() {
			this.$emit('closeModal');
			this.topic = '';
			this.message = '';
			this.showMessageResult = false;
		},
		showSendingModal() {
			return this.show && !this.showMessageResult;
		},
		async sendMessage() {
			if (this.message) {
				this.showMessageResult = true;

				await this.$apollo
					.mutate({
						mutation: gql`
							mutation($add: MessageAddInputObjectGraphType!) {
								addMessage(add: $add) {
									id
									text
									messageStatus
									sentByStudentId
									sentByUserId
								}
							}
						`,
						variables: {
							add: {
								sentByStudentId: this.userData.studentId,
								sentByUserId: this.userData.userId,
								messageStatus: 'Unread',
								text: this.message,
								topic: this.topic
							}
						}
					})
					.then(response => {
						if (response.data) {
							this.requestResult = 'success';
						}
					})
					.catch(() => {
						this.requestResult = 'failure';
					});
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.message-modal {
	.modal-content {
		width: 66%;
		margin: 64px auto;
		display: flex;
		flex-direction: column;
		text-align: left;
		h1 {
			text-align: center;
		}

		.failure {
			color: $font-red-alert;
		}

		.success {
			color: $icon-completed;
		}

		.textarea {
			margin-top: 16px;
		}

		.button-wrapper {
			text-align: center;
			margin-top: 32px;
		}
	}
}

.result-modal {
	::v-deep .modal {
		max-width: 600px;
		width: 40%;
	}
	.modal-content {
		text-align: center;

		.result-topic {
			word-break: break-word;
			color: $font-header;
			font-family: 'RobotoMedium';
			font-size: 24px;
			line-height: 32px;
			text-align: center;

			@media (min-width: $breakpoint-mid) {
				font-size: 26px;
			}
		}
		.result-message {
			word-break: break-word;
			color: $font-header;
			font-family: 'RobotoRegular';
			font-size: 14px;
			letter-spacing: 0;
			line-height: 23px;
			text-align: center;

			@media (min-width: $breakpoint-mid) {
				font-size: 16px;
			}
		}
	}

	.cancel {
		margin-left: 16px;
	}

	@media (max-width: 906px) {
		.cancel {
			margin-left: 0px;
			margin-top: 8px;
		}
	}
}
</style>
